import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CapitalizePipe } from '@orderna/admin-panel/src/app/common/pipes/capitalize/capitalize.pipe';
import {
  ButtonComponent,
  ButtonTextDirective,
} from '@orderna/admin-panel/src/app/shared/components/button/button.component';
import { InputSelectComponent } from '@orderna/admin-panel/src/app/shared/components/input/select/select.component';
import { ErrorListComponent } from '@orderna/admin-panel/src/app/shared/components/modal/error-list/error-list.component';
import { PAYMENT_TYPE_OPTIONS } from './constants/payment-type-options.constant';
import { PlanInterval as PlanInterval } from '@orderna/admin-panel/src/app/model/enum/plan-interval';
import { PaymentMethod } from '@orderna/admin-panel/src/app/model/enum/payment-method';
import { FindLastIneligiblePaymentPlanResponseDto } from '@orderna/admin-panel/src/app/services/payment-plans/dtos/find-last-ineligible-payment-plan.response.dto';
import { PaymentPlansApiService } from '@orderna/admin-panel/src/app/services/payment-plans/payment-plans-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PaymentStatus } from '@orderna/admin-panel/src/app/model/enum/payment-status.enum';
import { PlanStatus as PlanStatus } from '@orderna/admin-panel/src/app/model/enum/plan-status';
import { AuthApiService } from '@orderna/admin-panel/src/app/core/auth/auth-api.service';
import { Role } from '@orderna/admin-panel/src/app/model/enum/role';
import { PLAN_INTERVAL_OPTIONS } from './constants/plan-interval-options.constant';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingService } from '@orderna/admin-panel/src/app/services/shared/loader/loading.service';
import { PlansApiService } from '@orderna/admin-panel/src/app/services/plans/plans-api.service';
import { SubscriptionPlanOption } from '@orderna/admin-panel/src/app/model/subscription-plan-option';
import { PaymentsApiService } from '@orderna/admin-panel/src/app/services/payments/payments-api.service';
import { ChipsToggleComponent } from '@orderna/admin-panel/src/app/shared/components/chips-toggle/chips-toggle.component';
import { PlanType as PlanType } from '@orderna/admin-panel/src/app/model/enum/plan-type';

@Component({
  selector: 'app-subscription-required-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ChipsToggleComponent,
    InputSelectComponent,
    ButtonComponent,
    ButtonTextDirective,
    ErrorListComponent,
    CapitalizePipe,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './subscription-required-modal.component.html',
  styleUrls: [
    '/src/app/shared/components/modal/modal.component.scss',
    './subscription-required-modal.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionRequiredModalComponent {
  Role = Role;
  PlanType = PlanType;
  Validators = Validators;
  PAYMENT_TYPE_OPTIONS = PAYMENT_TYPE_OPTIONS;
  PLAN_INTERVAL_OPTIONS = PLAN_INTERVAL_OPTIONS;

  readonly dayPluralMapping: { [k: string]: string } = {
    '=1': '1 day ago',
    other: '# days ago',
  };
  readonly planIntervalMapping: { [k in PlanInterval]: string } = {
    [PlanInterval.MONTHLY]: '/month',
    [PlanInterval.SEMI_ANNUALLY]: '/6 months',
    [PlanInterval.ANNUALLY]: '/year',
  };

  #formBuilder = inject(FormBuilder);
  #auth = inject(AuthApiService);
  #paymentPlansService = inject(PaymentPlansApiService);
  #plansService = inject(PlansApiService);
  #paymentsService = inject(PaymentsApiService);
  #loadingService = inject(LoadingService);

  loading = this.#loadingService.loading;
  planIntervalFilter = signal<PlanInterval | null>(PlanInterval.MONTHLY);
  plans = signal<SubscriptionPlanOption[]>([]);
  errorMessages = signal<string[]>([]);
  lastPlan =
    signal<FindLastIneligiblePaymentPlanResponseDto | null>(null);

  filteredPlans = computed(() => {
    const intervalFilter = this.planIntervalFilter();
    return this.plans().filter(
      ({ interval }) => intervalFilter === null || intervalFilter === interval
    );
  });

  lastPlanName = computed(() => {
    const { type, interval } = this.lastPlan()?.plan ?? {};

    const capitalize = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1);

    return type && interval
      ? `${capitalize(type)} - ${capitalize(interval)}`
      : null;
  });
  hasNoPreviousSubscription = computed(
    () => this.lastPlan() === null
  );
  hasUnpaidSubscription = computed(
    () =>
      this.lastPlan()?.payment.status !== PaymentStatus.PAID
  );
  hasInactivePlan = computed(
    () => this.lastPlan()?.plan.status !== PlanStatus.ACTIVE
  );
  hasOverdueSubscription = computed(() => {
    const days = this.lastPlan()?.daysOverdue;
    return typeof days === 'number' && days > 0;
  });
  currentUserRole = computed(() => this.#auth.currentUser()?.role);

  form = this.#formBuilder.group({
    interval: this.#formBuilder.control<PlanInterval>(PlanInterval.ANNUALLY),
    planId: this.#formBuilder.control<number | null>(null),
    paymentMethod: this.#formBuilder.nonNullable.control<PaymentMethod>(
      PaymentMethod.GCASH
    ),
  });

  get submitButtonText() {
    const paymentMethod = this.PAYMENT_TYPE_OPTIONS.find(
      (o) => o.value === this.form.value.paymentMethod
    )?.label;

    const amount = this.selectedPlan?.price ?? 0;

    return `Pay ₱${amount} via ${paymentMethod}`;
  }

  get selectedPlan() {
    const planId = this.form.value.planId;
    if (!planId) return null;
    return this.plans().find((p) => p.id === planId);
  }

  async ngOnInit() {
    if (this.currentUserRole() !== Role.ADMIN) return;

    await Promise.all([this.loadLastIneligiblePaymentPlan(), this.loadPlans()]);

    this.initForm();
    this.initPlanIntervalFilter();
  }

  async loadLastIneligiblePaymentPlan() {
    try {
      const paymentPlan =
        await this.#paymentPlansService.findLastIneligiblePaymentPlan();
      this.lastPlan.set(paymentPlan);
    } catch (error: unknown) {
      const errorResponse = error as HttpErrorResponse;
      if (errorResponse.error.statusCode === 404) {
        this.lastPlan.set(null);
      } else {
        this.errorMessages.set(errorResponse.error.message);
      }
    }
  }

  async loadPlans() {
    try {
      const plans = await this.#plansService.findPlanNames();
      this.plans.set(plans);
    } catch (error: unknown) {
      const errorResponse = error as HttpErrorResponse;
      this.errorMessages.set(errorResponse.error.message);
    }
  }

  async initForm() {
    const lastPlan = this.lastPlan();
    const plans = this.plans();

    let initialPlanId = null;

    if (lastPlan) {
      initialPlanId = lastPlan.plan.id;
    } else if (plans.length > 0) {
      initialPlanId = this.plans()[0].id;
    }

    this.form.patchValue({
      planId: initialPlanId,
    });
  }

  async initPlanIntervalFilter() {
    const lastPlan = this.lastPlan();

    if (lastPlan) {
      this.planIntervalFilter.set(lastPlan.plan.interval);
    }
  }

  // Event handlers
  async onSubmit() {
    const planId = this.selectedPlan?.id;
    if (!planId) return;

    try {
      const { transactionPageUrl } =
        await this.#paymentsService.initiatePayment(planId);
      window.location.href = transactionPageUrl;
    } catch (error) {
      const errorResponse = error as HttpErrorResponse;
      this.errorMessages.set(errorResponse.error.message);
    }
  }

  onPlanIntervalFilterChanged(interval: PlanInterval | null) {
    this.planIntervalFilter.set(interval);

    const filteredPlans = this.plans().filter(
      (p) => interval === null || p.interval === interval
    );

    if (filteredPlans.length > 0) {
      this.form.patchValue({
        planId: filteredPlans[0].id,
      });
    }
  }
}

export function openSubscriptionRequiredModal(
  dialog: MatDialog,
  data: { email: string; daysOverdue: number }
): void {
  dialog.open(SubscriptionRequiredModalComponent, {
    id: 'subscription-required-modal',
    disableClose: true,
    data,
  });
}
